import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { CheckCircle, Package, ArrowRight, Loader2, Star } from "lucide-react";
import { trackPurchase } from "../utils/analytics";
import { useAuth } from "../App";
import { API } from "../apiConfig";


const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const { user } = useAuth();

  // Calculate loyalty points (10 points per $1)
  const pointsEarned = paymentData?.amount_total
    ? Math.floor(paymentData.amount_total / 100) * 10
    : 0;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        withCredentials: true
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Track purchase event
        trackPurchase(
          response.data.order_id,
          response.data.amount_total || 0,
          response.data.items || []
        );
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus("error");
      }
    }
  };

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(cents / 100);
  };

  return (
    <div className="min-h-screen" data-testid="checkout-success-page">
      <Navigation />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-xl mx-auto text-center">
          {status === "loading" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-medium mb-4">Processing Payment...</h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-secondary" />
              </div>
              
              <h1 className="text-4xl font-medium mb-4" data-testid="success-title">
                Thank You!
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Your order has been confirmed
              </p>

              {paymentData && (
                <div className="bg-card rounded-2xl border border-border p-6 mb-8 text-left">
                  <h2 className="font-medium mb-4">Order Details</h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order Total</span>
                      <span className="font-medium">{formatPrice(paymentData.amount_total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payment Status</span>
                      <span className="text-secondary font-medium capitalize">{paymentData.payment_status}</span>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-muted-foreground mb-8">
                A confirmation email has been sent to your inbox. 
                Your certificate of authenticity (if applicable) will be included with your order.
              </p>

              {/* Loyalty Points Earned */}
              {pointsEarned > 0 && user && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-primary/10 border border-primary/20 rounded-2xl p-6 mb-8"
                  data-testid="loyalty-points-earned"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Star className="h-5 w-5 text-primary fill-primary" />
                    <span className="text-lg font-medium text-primary">
                      You earned {pointsEarned.toLocaleString()} points!
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Collect points with every purchase. 500 points = $5 store credit.
                  </p>
                </motion.div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dashboard" data-testid="view-orders-btn">
                  <Button className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
                    <Package className="mr-2 h-4 w-4" />
                    View My Orders
                  </Button>
                </Link>
                <Link to="/products" data-testid="continue-shopping-btn">
                  <Button variant="outline" className="rounded-full px-8">
                    Continue Shopping
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {status === "expired" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h1 className="text-3xl font-medium mb-4">Session Expired</h1>
              <p className="text-muted-foreground mb-8">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/cart">
                <Button className="rounded-full px-8">Return to Cart</Button>
              </Link>
            </motion.div>
          )}

          {status === "timeout" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h1 className="text-3xl font-medium mb-4">Payment Processing</h1>
              <p className="text-muted-foreground mb-8">
                Your payment is still being processed. Please check your email for confirmation 
                or visit your orders page.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dashboard">
                  <Button className="rounded-full px-8">View Orders</Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="rounded-full px-8">Continue Shopping</Button>
                </Link>
              </div>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h1 className="text-3xl font-medium mb-4">Something went wrong</h1>
              <p className="text-muted-foreground mb-8">
                We couldn't verify your payment. Please contact support if you were charged.
              </p>
              <Link to="/cart">
                <Button className="rounded-full px-8">Return to Cart</Button>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
